import {
  Check,
  FilterList,
  Forward,
  KeyboardArrowUp,
  NavigateBefore,
  NavigateNext,
} from '@mui/icons-material';
import {
  alpha,
  Backdrop,
  Box,
  Checkbox,
  Divider,
  Fab,
  FormControl,
  FormControlLabel,
  MenuItem,
  Select,
  Table,
  TableProps,
  Theme,
  Tooltip,
  Typography,
  TypographyProps,
  useScrollTrigger,
  useTheme,
  Zoom,
} from '@mui/material';
import * as React from 'react';

import { createIndividualTableData } from '../../features/ViewModel';
import { getIconSize, Optional, returnIf } from '../../utils/Util';
import { AppLink } from './AppLink';
import IndividualTable from './IndividualTable';
import OptionalOptionButton, {
  OptionalOptionButtonProps,
} from './OptionalOptionButton';
import RequiredOptionButton, {
  RequiredOptionButtonProps,
} from './RequiredOptionButton';

export const StyledTable: React.FC<TableProps> = (props) => {
  const theme = useTheme();
  return (
    <div
      style={{
        display: 'flex', // StyledTableのマージンを含む横幅を要素内に収める
        overflowX: 'auto', // pageIndexは横スクロールさせない
        width: '100%', // 画面幅が640pxより少し小さい場合、100%を外すと表が横幅に合わない
      }}
    >
      <Table
        size={'small'}
        {...props}
        sx={{
          height: 'fit-content',
          boxShadow: 2,
          borderWidth: 0,
          borderColor: theme.palette.grey[300], //行間の色と同じ
          borderStyle: 'solid',
          m: 0.5, //マージンがないとboxShadowが表示されない
          ...props.sx,
        }}
      />
    </div>
  );
};

const requiredOptionTopButtonSx = {
  typography: 'body1',
  color: 'text.secondary',
  fontWeight: 900,
};

const requiredOptionTopButtonSelectedSx = {
  ...requiredOptionTopButtonSx,
  color: 'text.primary',
  textDecoration: 'underline',
  textUnderlineOffset: 4,
};

export const RequiredOptionTopTypography: React.FC<TypographyProps> = (
  props
) => (
  <Typography
    {...props}
    component={'span'}
    sx={{ ...requiredOptionTopButtonSelectedSx, ...props.sx }}
  />
);

export const RequiredOptionTopButton = (
  props: RequiredOptionButtonProps
): React.ReactElement => (
  <RequiredOptionButton
    {...props}
    sx={{
      ...props.sx,
      flexBasis: '100%', // 画面幅に寄らず、常に横幅100%で表示する
    }}
    buttonProps={{
      sx: requiredOptionTopButtonSx,
      variant: 'text',
    }}
    buttonPropsSelected={{
      sx: requiredOptionTopButtonSelectedSx,
    }}
  />
);

export const RequiredOptionSecondButton = (
  props: RequiredOptionButtonProps
): React.ReactElement => (
  <Box
    sx={{
      display: 'flex', // 横スクロールのためにdisplayとoverflowXが必要
      overflowX: 'auto', // はみ出した場合のみスクロールバーを表示
    }}
  >
    <RequiredOptionButton
      {...props}
      sx={{
        ...props.sx,
        m: 1,
        flexShrink: 0, // 画面幅が狭まっても各ボタンの横幅を維持する。はみ出したらBox要素によりスクロール可能になる
      }}
      buttonProps={{
        variant: 'outlined',
      }}
      buttonPropsSelected={{
        variant: 'contained',
      }}
    />
  </Box>
);

export const ActiveOnlyButton = (
  props: RequiredOptionButtonProps
): React.ReactElement => (
  <RequiredOptionButton
    {...props}
    sx={{ ...props.sx, m: 1 }}
    canUnselect={true}
    buttonProps={{
      startIcon: <Check sx={{ visibility: 'hidden' }} />,
      variant: 'outlined',
    }}
    buttonPropsSelected={{
      startIcon: <Check />,
      variant: 'contained',
    }}
  />
);

export const FilterButton = (
  props: OptionalOptionButtonProps
): React.ReactElement => (
  <OptionalOptionButton {...props} sx={{ m: 1 }} startIcon={<FilterList />} />
);

interface CheckboxLabelProps {
  displayName: string;
  checked: boolean;
  onChanged: (checked: boolean) => void;
}

export const CheckboxLabel = ({
  displayName,
  checked,
  onChanged,
}: CheckboxLabelProps): React.ReactElement => (
  // 達成レースをテーブル内に表示するか切り替えるチェックボックス
  <FormControlLabel
    sx={{ m: 0 }}
    control={
      <Checkbox
        defaultChecked={checked}
        onChange={(e) => {
          onChanged(e.target.checked);
        }}
        size="small"
        color="primary"
      ></Checkbox>
    }
    label={<Typography variant="body2">{displayName}</Typography>}
  />
);

type RankDiffIconProps = JSX.IntrinsicElements['span'] & {
  diffValue: Optional<string | number>;
  theme: Theme;
};

export const RankDiffIcon = ({
  diffValue,
  theme,
  ...props
}: RankDiffIconProps): React.ReactElement => {
  let rankDiffElement = <></>;
  let num = NaN;
  if (typeof diffValue === 'number') {
    num = diffValue;
  } else if (typeof diffValue === 'string') {
    num = parseInt(diffValue);
  }
  if (num !== 0 && !isNaN(num)) {
    const color =
      num > 0 ? theme.palette.ranking.down.main : theme.palette.ranking.up.main;
    // 右向きアイコンを上または下に回転させる
    const transform = `rotate(${num > 0 ? '' : '-'}90deg)`;
    const absNum = num > 0 ? num : -num;

    const iconSize = getIconSize(theme.typography.body3.fontSize);
    rankDiffElement = (
      <>
        <Forward
          key="icon"
          style={{ transform }}
          sx={{
            color,
            fontSize: iconSize,
          }}
        />
        <Typography key="label" component="span" variant="body3" sx={{ color }}>
          {absNum}
        </Typography>
      </>
    );
  }

  return (
    // display: flex で囲むことで改行回避
    <span {...props} style={{ display: 'flex' }}>
      <span key="children">{props.children}</span>
      {rankDiffElement}
    </span>
  );
};

export const NewIcon: React.FC<TypographyProps> = (props) => {
  const theme = useTheme();
  return (
    <Typography
      {...props}
      component="span"
      sx={{
        color: theme.palette.background.paper,
        backgroundColor: theme.palette.primary.main,
        padding: '0px 4px 0px 4px',
        borderRadius: '6px',
        fontSize: '0.625rem',
        ...props.sx,
      }}
    >
      {'New'}
    </Typography>
  );
};

type ColumnTooltipProps = {
  label: string;
  children?: React.ReactNode;
};

export const ColumnTooltip: React.FC<ColumnTooltipProps> = ({
  label,
  children,
}): React.ReactElement => (
  <Tooltip
    title={label}
    placement="top-start"
    arrow={false}
    enterTouchDelay={0}
  >
    <span
      style={{
        textDecorationLine: 'underline',
        textDecorationStyle: 'dotted',
      }}
    >
      {children}
    </span>
  </Tooltip>
);

type StyledSelectProps = {
  valueLabelMap: Record<string, string>;
  selectedValue: string;
};

export const StyledSelect: React.FC<StyledSelectProps> = ({
  valueLabelMap,
  selectedValue,
}) => {
  const theme = useTheme();
  const borderColor = alpha(theme.palette.primary.main, 0.5);
  const menuItems = Object.entries(valueLabelMap).map(([value, label]) => (
    <MenuItem key={value} value={value}>
      {label}
    </MenuItem>
  ));
  return (
    <FormControl sx={{ m: 1 }}>
      <Select
        value={selectedValue}
        autoWidth
        sx={{
          height: '100%',
          fontSize: '0.875em',
          '& .MuiOutlinedInput-input': {
            padding: '0px 14px',
          },
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor,
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor,
          },
        }}
      >
        {menuItems}
      </Select>
    </FormControl>
  );
};

export const TableDescriptions: React.FC<{ descriptions: string[] }> = ({
  descriptions,
}) => (
  <Box sx={{ m: 1 }}>
    {descriptions.map((description, index) => (
      <Typography key={index} variant="body2">
        {description}
      </Typography>
    ))}
  </Box>
);

type AreaProps = {
  id?: string;
  title: React.ReactNode;
  children?: React.ReactNode;
  breadCrumps?: React.ReactNode;
};

// H1
export const PageArea: React.FC<AreaProps> = ({
  title,
  children,
  breadCrumps,
}) => (
  <>
    <Box sx={{ flexBasis: '100%', m: 1 }}>{breadCrumps}</Box>
    <Typography
      variant={'h1'}
      sx={{
        flexBasis: '100%',
        m: 1,
      }}
    >
      {title}
    </Typography>
    <PageDivider />
    {children}
  </>
);

// H2
export const TableArea = React.forwardRef<HTMLDivElement, AreaProps>(
  ({ id, title, children }, ref): React.ReactElement => {
    const theme = useTheme();
    return (
      <>
        <Box
          id={id}
          ref={ref}
          style={{
            // ヘッダーに隠れないようその高さ分だけ下にずらす
            scrollMarginTop: '72px',
          }}
          sx={{
            display: 'flex',
            flexBasis: '100%',
            m: 1,
            px: 1,
            borderLeftColor: theme.palette.primary.main,
            borderLeftWidth: '6px',
            borderLeftStyle: 'solid',
          }}
        >
          <Typography variant={'h2'}>{title}</Typography>
        </Box>
        {children}
        <Box sx={{ flexBasis: '100%', m: 2 }} />
      </>
    );
  }
);
TableArea.displayName = 'TableArea';

// H3
export const H3: React.FC<AreaProps> = ({ title, children }) => (
  <>
    <Typography
      variant={'h3'}
      sx={{
        flexBasis: '100%',
        m: 1,
        px: 1,
      }}
    >
      {title}
    </Typography>
    {children}
    <Box sx={{ flexBasis: '100%', m: 1 }} />
  </>
);

export const ScrollTopFab: React.FC = () => {
  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 100,
  });
  const scrollToTop = React.useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);
  return (
    <Zoom in={trigger}>
      <Box
        role="presentation"
        sx={{ position: 'fixed', bottom: 32, right: 32, zIndex: 1 }}
      >
        <Fab onClick={scrollToTop} color="primary" size="small">
          <KeyboardArrowUp />
        </Fab>
      </Box>
    </Zoom>
  );
};

export const TableBackDrop: React.FC<{ open: boolean }> = ({ open }) => (
  <Backdrop data-testid="tableBackDrop" open={open} sx={{ zIndex: 'modal' }} />
);

interface PaginationButtonProps {
  label: string;
  path: string;
}

const PaginationButton: React.FC<PaginationButtonProps & { isPrev: boolean }> =
  ({ label, path, isPrev }) => {
    const startIcon = isPrev ? <NavigateBefore /> : undefined;
    const endIcon = isPrev ? undefined : <NavigateNext />;
    return (
      <AppLink to={path}>
        <Box display="flex" alignItems="center">
          {startIcon}
          <Typography variant="body2">{label}</Typography>
          {endIcon}
        </Box>
      </AppLink>
    );
  };

interface PaginationProps {
  prevButtonProps: Optional<PaginationButtonProps>;
  nextButtonProps: Optional<PaginationButtonProps>;
}
export const Pagination: React.FC<PaginationProps> = ({
  prevButtonProps,
  nextButtonProps,
}) => {
  const prevButton = returnIf(prevButtonProps, (props) => (
    <PaginationButton {...props} isPrev={true} />
  ));
  const nextButton = returnIf(nextButtonProps, (props) => (
    <PaginationButton {...props} isPrev={false} />
  ));
  return (
    <Box
      display="flex"
      flexDirection="row"
      sx={{
        mx: 1,
        alignItems: 'center',
      }}
    >
      {prevButton}
      {/* ボタンを左端と右端に置くためのスペース */}
      <Box sx={{ m: 1, flexGrow: 1 }} />
      {nextButton}
    </Box>
  );
};

export const PageDivider: React.FC = () => {
  const theme = useTheme();
  return (
    <Divider
      sx={{
        mx: 1,
        borderBottomColor: theme.palette.grey[600],
        borderBottomWidth: '1px',
        borderBottomStyle: 'solid',
      }}
    />
  );
};

export const HistoryItems: React.FC<{
  items: [React.ReactElement, React.ReactElement[]][];
  visibleCount: number;
}> = ({ items, visibleCount }) => (
  <Typography component="div" variant="body2">
    <ul style={{ margin: '0px' }}>
      {items.map(([key, subItems], index) => {
        const style = index >= visibleCount ? { display: 'none' } : {};
        return (
          <li key={index} style={style}>
            {key}
            <ul>
              {subItems.map((subItem, index) => (
                <li key={index}>{subItem}</li>
              ))}
            </ul>
          </li>
        );
      })}
    </ul>
  </Typography>
);
const PreviewStyledComponents = (): React.ReactElement => {
  const theme = useTheme();
  const [filterButtonState, setFilterButtonState] = React.useState<
    Record<string, string>
  >({});
  const profileData = createIndividualTableData({
    columns: [
      {
        title: 'name',
        hasMissingData: false,
      },
      {
        title: 'value',
        hasMissingData: false,
      },
    ],
    rows: [
      {
        id: 'a',
        cells: [
          {
            values: [
              {
                value: 'birthday',
                additionalValues: {},
              },
            ],
          },
          { values: [{ value: '1999/12/31', additionalValues: {} }] },
        ],
      },
      {
        id: 'b',
        cells: [
          {
            values: [{ value: 'circuit', additionalValues: {} }],
          },
          { values: [{ value: '鈴鹿サーキット', additionalValues: {} }] },
        ],
      },
    ],
  });

  const profileData2 = createIndividualTableData({
    columns: [
      { title: 'name', hasMissingData: false },
      { title: 'value', hasMissingData: false },
    ],
    rows: [
      {
        id: 'a',
        cells: [
          {
            values: [{ value: 'birthday', additionalValues: {} }],
          },
          {
            values: [
              {
                value:
                  '1999/12/31 too long sentence this is. Maybe it happens if circuit name or race name is too long.',
                additionalValues: {},
              },
            ],
          },
        ],
      },
    ],
  });
  return (
    <>
      <Box
        sx={{ display: 'inline-flex', flexDirection: 'row', flexWrap: 'wrap' }}
      >
        <PageArea title="ドライバーランキング">
          <TableArea title="プロフィール">
            <IndividualTable tableData={profileData} isLoading={false} />
          </TableArea>
          <TableArea title="プロフィール">
            <IndividualTable tableData={profileData2} isLoading={false} />
          </TableArea>
          <TableArea title="ヒストリー">
            <HistoryItems
              items={[
                [
                  <>2002年</>,
                  [
                    <>
                      第1戦: GT300クラス初出場(123人目)、初表彰台(74人目)
                      両クラス勝利達成
                    </>,
                    <>
                      第8戦:
                      GT300クラス初勝利(53人目)、年間チャンピオン達成(23人目)
                    </>,
                  ],
                ],
              ]}
              visibleCount={1}
            />
          </TableArea>
          <TableArea title="経歴">
            <Box
              sx={{
                display: 'flex', // flexレイアウトにしないと現役ボタンなどのY位置がずれる
                flexDirection: 'row', // 横方向に並べる
                flexWrap: 'wrap', // はみ出したら改行。メーカー別など
              }}
            >
              <RequiredOptionTopButton
                values={[
                  { id: 'GT300', value: 'GT300', available: true },
                  { id: 'GT500', value: 'GT500', available: true },
                ]}
                selectedId={'GT500'}
              />
              <RequiredOptionSecondButton
                values={[
                  { id: 'a', value: '通算', available: true },
                  { id: 'b', value: '連続', available: true },
                  { id: 'c', value: '達成率', available: true },
                  { id: 'd', value: 'その他', available: true },
                ]}
                selectedId={'a'}
              />
              <ActiveOnlyButton
                values={[{ id: 'a', value: '現役のみ', available: true }]}
                selectedId={''}
              />
              <FilterButton
                defaultName={'絞り込み'}
                values={[
                  {
                    id: 'a',
                    displayName: 'タイヤ',
                    values: [
                      { id: 'a1', displayName: 'ヨコハマ', available: true },
                      {
                        id: 'a2',
                        displayName: 'ブリヂストン',
                        available: true,
                      },
                      { id: 'a3', displayName: 'ダンロップ', available: false },
                    ],
                  },
                  {
                    id: 'b',
                    displayName: 'メーカー',
                    values: [
                      { id: 'b1', displayName: 'トヨタ', available: true },
                      { id: 'b2', displayName: 'ホンダ', available: true },
                      {
                        id: 'b3',
                        displayName: 'マクラーレン',
                        available: false,
                      },
                    ],
                  },
                ]}
                selectedId={filterButtonState}
                onChanged={(s) => setFilterButtonState(s)}
              />
              <StyledSelect
                valueLabelMap={{
                  sum: '通算記録',
                  cont: '連続記録',
                  average: '達成率',
                  firstSeason: '初参戦年',
                  minAge: '最年少記録',
                  maxAge: '最年長記録',
                  other: 'その他',
                }}
                selectedValue="sum"
              />
              <CheckboxLabel
                displayName="達成レースをテーブル内に表示"
                checked={true}
                onChanged={() => {
                  // do nothing
                }}
              />
            </Box>
          </TableArea>
        </PageArea>
      </Box>
      {[...new Array(2).keys()].map((num) => (
        <span key={num}>
          <RankDiffIcon diffValue={num - 1} theme={theme} />
        </span>
      ))}
      <Pagination
        prevButtonProps={{ label: 'HELM MOTOR SPORTS', path: 'race/race_id/1' }}
        nextButtonProps={{
          label: 'HYPER WATER Racing',
          path: 'race/race_id/2',
        }}
      />
    </>
  );
};

export default PreviewStyledComponents;
